<template>
    <div
        :class="[swatchClassName, 'side-navigation-menu']"
        @click="forwardClick"
    >
        <slot/>
        <div class="menu-container">
            <DxTreeView
                ref="treeViewRef"
                :items="items"
                key-expr="path"
                selection-mode="single"
                :focus-state-enabled="false"
                :search-enabled="isLargeScreen && !compactMode"
                expand-event="click"
                @item-click="handleItemClick"
                width="100%"
            />
        </div>
    </div>
</template>

<script>
import DxTreeView from 'devextreme-vue/tree-view';
import {sizes} from '@/utils/media-query';
import appConfig from "@/utils/app-config";
import {onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {themeService} from '@/theme-service';

export default {
    props: {
        compactMode: Boolean
    },
    setup(props, context) {
        const route = useRoute();
        const router = useRouter();

        const isLargeScreen = sizes()['screen-large'];

        const navigation = appConfig.menu;
        const items = navigation.map((item) => {
            if (item.path && !(/^\//.test(item.path))) {
                item.path = `/${item.path}`;
            }
            return {...item, expanded: isLargeScreen}
        });

        const treeViewRef = ref(null);
        const swatchClassName = ref('');

        function forwardClick(...args) {
            context.emit("click", args);
        }

        function handleItemClick(e) {
            if (!e.itemData.path || props.compactMode) {
                return;
            }
            router.push(e.itemData.path);

            const pointerEvent = e.event;
            pointerEvent.stopPropagation();
        }

        function updateSelection() {
            if (!treeViewRef.value || !treeViewRef.value.instance) {
                return;
            }

            treeViewRef.value.instance.selectItem(route.path);
            treeViewRef.value.instance.expandItem(route.path);
        }

        onMounted(() => {
            updateSelection();
            if (props.compactMode) {
                treeViewRef.value.instance.collapseAll();
            }
        });


        watch(
            () => route.path,
            () => {
                updateSelection();
            }
        );

        watch(
            () => themeService.isDark,
            () => {
                swatchClassName.value = themeService.isDark.value ? 'dx-swatch-additional-dark' : 'dx-swatch-additional';
            },
            {immediate: true}
        );

        watch(
            () => props.compactMode,
            () => {
                if (props.compactMode) {
                    treeViewRef.value.instance.collapseAll();
                } else {
                    updateSelection();
                }
            }
        );

        return {
            treeViewRef,
            items,
            forwardClick,
            handleItemClick,
            updateSelection,
            swatchClassName,
            isLargeScreen
        };
    },
    components: {
        DxTreeView
    }
};
</script>

<style lang="scss">
@import "../variables.scss";
@import "../dx-styles.scss";

.dx-swatch-additional, .dx-swatch-additional-dark {
    &.side-navigation-menu {
        display: flex;
        flex-direction: column;
        min-height: 100%;
        height: 100%;
        width: 250px !important;
        background-color: var(--menu-bg);

        .menu-container {
            min-height: 100%;
            margin-top:0;
            display: flex;
            flex: 1;


            .dx-treeview {
                // ## Long text positioning
                font-size: 13px;
                margin-top: 5px;
                white-space: normal;
                // ##

                .dx-treeview-node-container:empty {
                    display: none;
                }

                // ## Icon width customization
                .dx-treeview-item {
                    border-radius: 0;
                    flex-direction: row-reverse;
                    padding: 6px 5px 6px 0;

                    .dx-icon {
                        width: calc($side-panel-min-width) !important;
                        color: var(--base-accent);
                        margin: 0 !important;
                    }
                }

                // ##

                // ## Arrow customization
                .dx-treeview-node {
                    padding: 0 0 !important;
                }

                .dx-treeview-toggle-item-visibility {
                    right: 10px;
                    left: auto;
                }

                .dx-rtl .dx-treeview-toggle-item-visibility {
                    left: 10px;
                    right: auto;
                }

                // ##

                // ## Item levels customization
                .dx-treeview-node {
                    &[aria-level="1"] {
                        font-weight: 600;
                    }

                    &[aria-level="2"] .dx-treeview-item-content {
                        font-weight: normal;
                        //padding: 0 0 0 calc($side-panel-min-width + 10px);
                        padding: 0 0 0 13px;
                        .dx-icon {
                            width: calc($side-panel-min-width) !important;
                            margin: 0 !important;
                        }
                    }

                    &[aria-level="3"] .dx-treeview-item-content {
                        font-weight: normal;
                        padding: 0 0 0 $side-panel-min-width;
                    }
                }

                // ##
            }
        }
    }

    .dx-treeview-search {
        margin-top: 5px;
        margin-bottom: 5px;
        padding-bottom: 5px;
        //border: 0 !important;
        background-color: var(--menu-bg) !important;
        border-top: 0 !important;
        border-left: 0 !important;
        border-right: 0 !important;
        border-bottom: 1px solid var(--treeview-search-border) !important;
        border-radius: 0 !important;
    }
}
</style>
